import createClient from "openapi-fetch";
import type { paths } from "./api/types";

/**
 * Canonical name of the client-info request header (tier-0 version census +
 * tier-1 operational beacons — see `infra/CLIENT_TELEMETRY_PROPAGATION.md`).
 *
 * Declared HERE, in the package that actually writes it, and imported by
 * everyone else. The Rust side pins the same literal in
 * `packages/core-rust/src/db/client_telemetry.rs` (`CLIENT_INFO_HEADER`), and
 * `apps/server/src/main.rs` must keep it in the CORS `allow_headers` allowlist —
 * a browser drops a non-allowlisted custom header on a cross-origin request
 * without a word, and the census would read "100% unknown" forever.
 */
export const CLIENT_INFO_HEADER = "X-Client-Info";

/**
 * Structural type for the `fetch` we hand to openapi-fetch.
 *
 * We deliberately do NOT use `typeof fetch` here because that pulls in the
 * runtime's full `fetch` interface — including Bun's `preconnect` extension
 * — which our wrapper doesn't implement. The signature inlines
 * `RequestInfo` as `Request | string` to stay portable across Bun / Node /
 * browser tsconfigs (some of which type `RequestInfo` as `any`).
 */
type ApiFetch = (input: Request | string | URL, init?: RequestInit) => Promise<Response>;

/**
 * `fetch` credentials mode. Inlined (instead of the DOM `RequestCredentials`)
 * because this package's tsconfig doesn't pull in the DOM lib — it must stay
 * portable across Bun / Node / browser typings.
 */
type Credentials = "omit" | "same-origin" | "include";

export interface CreateApiClientOptions {
  baseUrl: string;
  /**
   * Injectable transport. When provided it REPLACES the built-in
   * {@link buildFetch} entirely (so `getAuthToken` / `getActiveAccountId` /
   * `credentials` no longer apply — the custom transport owns request shaping).
   *
   * This is the native "HttpOnly" seam: Tauri passes a transport that routes
   * every request through the Rust `api_request` command, which injects
   * `Authorization: Bearer` from the OS secure store so the token never lives in
   * the WebView JS. Web leaves this unset and keeps the same-origin cookie
   * transport below.
   */
  fetch?: ApiFetch;
  /**
   * Returns the bearer token to attach as `Authorization: Bearer …` on every
   * request, or `null` when there's no active session.
   *
   * Why this exists: in cross-site WebView contexts (Tauri at `tauri.localhost`
   * talking to a backend at `localhost:3333`, browser extensions, etc.) the
   * `SameSite=Lax` session cookie set by the backend is **dropped** by the
   * WebView on subsequent fetches. Bearer auth gives us a transport that
   * survives those origins.
   *
   * Same-origin web (Vite dev proxy, prod served from the same host as the
   * API) can leave this unset — cookies still work via `credentials: "include"`.
   */
  getAuthToken?: () => string | null;
  /**
   * Returns the **active account's userId** to send as the `X-Active-Account`
   * selector header, or `null` when this tab has no real active account (an
   * explicit guest, or before login).
   *
   * This is the cookie-BFF (web) transport: the backend resolves the request's
   * session from the HttpOnly `mealplan.device` cookie + this selector — picking
   * which of the device's logged-in accounts is active for THIS tab. The custom
   * header also doubles as the CSRF defense (a cross-site forgery cannot set
   * it). `null` → no header → the device branch is unauthenticated (clean
   * guest, no cross-account leak). Tauri leaves this unset (it authenticates
   * directly via Bearer, no selector needed).
   */
  getActiveAccountId?: () => string | null;
  /**
   * `fetch` credentials mode for EVERY request this client makes.
   *
   * Web (cookie-BFF) passes `"include"`: the request carries the HttpOnly
   * `mealplan.device` cookie, and the active account is selected by the
   * `X-Active-Account` header (see {@link getActiveAccountId}). The token no
   * longer lives in JS — it never leaves the server — so the whole Bearer-null /
   * registration-race class of bugs disappears.
   *
   * Tauri / native also use `"include"`: harmless there (the cross-site
   * `SameSite=Lax` cookie is dropped by the WebView anyway, so Bearer — via
   * {@link getAuthToken} — is mandatory and remains the only transport).
   *
   * The OAuth flow does NOT depend on this: the provider redirect, the flow
   * `state` cookie and the callback `#token=…` are all TOP-LEVEL navigations
   * (real `<a href>` / 302), never `fetch`.
   */
  credentials?: Credentials;
  /**
   * Returns the value for the {@link CLIENT_INFO_HEADER} header — this build's
   * version/platform plus the tier-1 beacons — or `null` to send nothing.
   *
   * Attached by an openapi-fetch `onRequest` middleware rather than inside
   * {@link buildFetch}, and that placement is the whole point: on Tauri the
   * `fetch` option REPLACES `buildFetch` entirely (the Rust `api_request`
   * proxy), so a header added there would never leave a desktop or Android
   * build. Since the APK is the reason a version census exists at all — an
   * installed APK can sit on one version for months — a web-only census would
   * have been worse than none. Middleware runs before the transport on both
   * platforms, so one insertion point covers both.
   */
  getClientInfo?: () => string | null;
  /**
   * Called when a response comes back `401` on a request the caller believed was
   * authenticated (see {@link isAuthenticatedHint}).
   *
   * Exists because of a real incident: a 401 caused by auth-transport drift was
   * surfaced to the user as "Server Offline", and it cost hours to diagnose
   * because nothing counted it. This is that counter.
   */
  onAuthTransportFailure?: () => void;
  /**
   * Whether the app currently believes it has an active account. Used only to
   * decide whether a `401` is an anomaly (session drift) or the perfectly normal
   * answer for a guest. Without it every logged-out visitor would inflate the
   * auth-failure beacon and make it useless.
   */
  isAuthenticatedHint?: () => boolean;
}

export function buildFetch(
  getAuthToken?: () => string | null,
  credentials: Credentials = "include",
  getActiveAccountId?: () => string | null,
): ApiFetch {
  // The `ApiFetch` annotation lets TS infer `input`/`init` types directly,
  // so we never need an `as`/`unknown` round-trip.
  const wrapped: ApiFetch = async (input, init) => {
    // Two mutually-exclusive transports share this builder:
    //   • Tauri/native → `Authorization: Bearer <token>` (cross-site cookie is
    //     dropped by the WebView).
    //   • Web (cookie-BFF) → `X-Active-Account: <userId>` selector + the device
    //     cookie that rides `credentials: "include"`.
    const token = getAuthToken?.() ?? null;
    const activeAccountId = getActiveAccountId?.() ?? null;

    // Nothing to add → keep the request untouched. openapi-fetch hands us a
    // pre-built `Request` object as `input`; if we'd spread an empty `headers`
    // field into `init`, fetch would **wipe** the Request's headers (including
    // `Content-Type: application/json`), and the backend would 415.
    //
    // `credentials` MUST go LAST so a `credentials` key inside `init`
    // (openapi-fetch may pass one, even `undefined`) can never override the
    // client's chosen mode. For web (`"include"`) this preserves the device
    // cookie even on a selector-less guest request (which then stays cleanly
    // unauthenticated). For Tauri it preserves the legacy transport.
    if (token === null && activeAccountId === null) {
      return fetch(input, { ...init, credentials });
    }

    // We have a header to add. Pull the existing headers from the Request when
    // the caller passed one (openapi-fetch does), otherwise from `init`.
    const existingHeaders = input instanceof Request ? input.headers : init?.headers;
    const headers = new Headers(existingHeaders);
    if (token !== null && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    if (activeAccountId !== null && !headers.has("X-Active-Account")) {
      headers.set("X-Active-Account", activeAccountId);
    }

    // For Request input, clone it with the augmented headers so the body
    // / method / etc. survive. Plain URL/string input goes through fetch
    // directly with the merged init.
    if (input instanceof Request) {
      const cloned = new Request(input, { headers });
      return fetch(cloned, { ...init, credentials });
    }
    return fetch(input, { ...init, headers, credentials });
  };
  return wrapped;
}

export const createApiClient = (options: CreateApiClientOptions) => {
  const client = createClient<paths>({
    baseUrl: options.baseUrl,
    fetch:
      options.fetch ??
      buildFetch(options.getAuthToken, options.credentials, options.getActiveAccountId),
  });

  // Client telemetry: stamp this build's identity + beacons on every request.
  // Runs BEFORE the transport, so it covers the Tauri/native `fetch` override
  // too (see `getClientInfo`). Wrapped in try/catch because telemetry must never
  // be able to stop a user's request: if building the header throws, the request
  // goes out without it and the census simply counts that device as unknown.
  client.use({
    onRequest({ request }) {
      try {
        const info = options.getClientInfo?.() ?? null;
        if (info !== null && info !== "") {
          request.headers.set(CLIENT_INFO_HEADER, info);
        }
      } catch {
        // Deliberately silent: a broken beacon is not the user's problem.
      }
      return request;
    },
  });

  // Transversal safety net. A gateway/proxy failure (502/503/504) or any
  // non-2xx whose body isn't JSON makes openapi-fetch resolve with BOTH `data`
  // and `error` undefined. Every `if (error)` guard at the call sites then
  // silently passes, and code that reads `data.<field>` throws
  // ("Cannot read properties of undefined"). Normalising such responses into a
  // structured error here — once — guarantees a populated `error` for all
  // callers instead of patching each call.
  client.use({
    onResponse({ response }) {
      if (response.ok) {
        return undefined;
      }
      // Auth-transport beacon. Counted only when we believed we were logged in;
      // a guest's 401 is the correct answer, not a failure.
      if (response.status === 401) {
        try {
          if (options.isAuthenticatedHint?.() === true) {
            options.onAuthTransportFailure?.();
          }
        } catch {
          // Same rule as above: telemetry never breaks the response path.
        }
      }
      const contentType = response.headers.get("content-type") ?? "";
      if (contentType.includes("json")) {
        // openapi-fetch can already parse a structured error body.
        return undefined;
      }
      const body = JSON.stringify({
        status: response.status,
        title: response.statusText || "Request failed",
        detail: `The server responded with ${response.status} and a non-JSON body.`,
      });
      return new Response(body, {
        status: response.status,
        statusText: response.statusText,
        headers: { "content-type": "application/problem+json" },
      });
    },
  });

  return client;
};

export type ApiClient = ReturnType<typeof createApiClient>;
